import { Temporal } from 'temporal-polyfill';
import { BUILTINS } from './builtins';
import { toPlainDate } from './dates';
import { CallArg, FormulaRegistry, FunctionSpec, paramAt } from './registry';
import { AstNode, FormulaError, isFormulaError, ValueType } from './types';
import {
  BLANK,
  compareValues,
  EvalValue,
  finiteOrNumError,
  FormulaValue,
  ListItem,
  requireBoolean,
  requireNumber,
  toValue,
  typeOfValue,
  valuesEqual,
} from './values';

export type { FormulaValue, ListItem } from './values';

/**
 * Supplies a field's value for one evaluation (typically one grid row).
 * Blank (null/undefined, empty text, or a NaN) is missing data: it propagates
 * through arithmetic, ordering and functions, reads as "" under & and equals
 * only another blank under =. A list field resolves to its items; lists only
 * mean something as REDUCE's second argument, SIZE's argument, or INDEX's or
 * SUM's first argument, and are #VALUE! anywhere else.
 * Returning a FormulaError makes the ref evaluate to that error — Excel's
 * "referencing an error cell yields the error", IFERROR-catchable — which is
 * how a referenced formula field's own failure propagates.
 */
export type FieldResolver = (key: string) => FormulaValue | ListItem[] | FormulaError | null | undefined;

/**
 * A field's declared type, so the engine can read the row's raw value as it:
 * ISO text or an epoch number as a date, a JS boolean as TRUE/FALSE. Without
 * it a value keeps the type it arrives with.
 */
export type FieldTypeLookup = (ref: string) => ValueType | undefined;

/**
 * Values supplied by the evaluating surface, not the row — the formula
 * language's environment. Optional everywhere: the engine defaults each
 * value (ASOFDATE falls back to today), so only surfaces with a meaningful
 * context need to pass one.
 */
export interface EvalContext {
  /** ISO date (YYYY-MM-DD) the surface is viewing data as of; read by ASOFDATE(). */
  asOfDate?: string;
  /** Declared field types, for reading raw values as dates and booleans. */
  fieldType?: FieldTypeLookup;
}

/**
 * A name in scope mid-evaluation: a REDUCE in progress — its accumulator
 * binding and the current item of its list — or one LET binding.
 */
type Frame =
  | { kind: 'reduce'; param: string; acc: EvalValue; list: string; item: ListItem | undefined }
  | { kind: 'let'; param: string; value: EvalValue };

interface Runtime {
  resolveField: FieldResolver;
  frames: Frame[];
  registry: FormulaRegistry;
  context?: EvalContext;
}

/**
 * Evaluate an AST against a resolver. Never throws: failures are returned as
 * Excel-style FormulaError values and propagate through every operator and
 * function except IFERROR, which catches them. A date result leaves as ISO
 * text (YYYY-MM-DD); TRUE/FALSE leaves as a boolean. Blanks propagate the same way
 * — a blank operand or argument makes the result blank — except under =, <>,
 * CONCAT, ISBLANK and IFBLANK, which are how a formula asks about or replaces one.
 * An error beats a blank wherever both are present.
 *
 * Text never becomes a number on its own: arithmetic over text is #VALUE!
 * and VALUE() is the cast. CONCAT (& in the Excel spelling) joins anything
 * as text; = and <> compare text
 * case-insensitively and never equate text with numbers; the relational
 * operators order two numbers or two texts and are #VALUE! across the two.
 */
export function evaluate(
  ast: AstNode,
  resolveField: FieldResolver,
  context?: EvalContext,
  registry: FormulaRegistry = BUILTINS
): FormulaValue | FormulaError | null {
  const result = evaluateNode(ast, { resolveField, frames: [], registry, context });
  if (result === BLANK) {
    return null;
  }
  return result instanceof Temporal.PlainDate ? result.toString() : result;
}

/**
 * A raw row value as the engine reads it for `ref`: blank-normalized, then
 * converted to the ref's declared type where the surface declares one — a
 * date field's ISO text or epoch number becomes a date, a boolean field must
 * hold TRUE/FALSE, a text field's number reads as its digits. A mismatch is
 * #VALUE!, the runtime mirror of the type rule.
 */
function readValue(raw: unknown, ref: string, rt: Runtime): EvalValue | FormulaError {
  const value = toValue(raw, ref);
  if (isFormulaError(value) || value === BLANK) {
    return value;
  }
  const declared = rt.context?.fieldType?.(ref);
  if (!declared || declared === typeOfValue(value)) {
    return value;
  }
  if (declared === 'date') {
    return toPlainDate(value);
  }
  if (declared === 'string' && typeof value === 'number') {
    return String(value);
  }
  return new FormulaError('#VALUE!', `Field [${ref}] holds ${typeOfValue(value)} data, not ${declared}`);
}

function evaluateNode(ast: AstNode, rt: Runtime): EvalValue | FormulaError {
  switch (ast.kind) {
    case 'num':
      return ast.value;
    case 'str':
      return ast.value;
    case 'bool':
      return ast.value;
    case 'field':
      return readValue(rawFieldValue(ast.ref, rt), ast.ref, rt);
    case 'param': {
      const frame = rt.frames.findLast((candidate) => candidate.param === ast.name);
      if (!frame) {
        return new FormulaError('#ERROR!', `Unknown name "${ast.name}"`);
      }
      return frame.kind === 'reduce' ? frame.acc : frame.value;
    }
    case 'unary': {
      const operand = evaluateNode(ast.operand, rt);
      if (isFormulaError(operand) || operand === BLANK) {
        return operand;
      }
      const number = requireNumber(operand);
      if (isFormulaError(number)) {
        return number;
      }
      return ast.op === '-' ? -number : number;
    }
    case 'binary': {
      const left = evaluateNode(ast.left, rt);
      if (isFormulaError(left)) {
        return left;
      }
      const right = evaluateNode(ast.right, rt);
      if (isFormulaError(right)) {
        return right;
      }
      return applyBinary(ast.op, left, right);
    }
    case 'call':
      return applyCall(ast, rt);
    case 'lambda':
      return new FormulaError('#ERROR!', 'LAMBDA can only be used as the last argument of REDUCE');
    case 'let':
      return applyLet(ast, rt);
  }
}

/**
 * LET(name, value, …, calculation): bind each value in declaration order —
 * eagerly, so the first failing value is the result whether or not its name
 * is read — then evaluate the calculation with every name in scope. A blank
 * value stays blank through its name, like a blank field.
 */
function applyLet(ast: Extract<AstNode, { kind: 'let' }>, rt: Runtime): EvalValue | FormulaError {
  let bound = 0;
  let result: EvalValue | FormulaError | undefined;
  for (const { name, value } of ast.bindings) {
    const evaluated = evaluateNode(value, rt);
    if (isFormulaError(evaluated)) {
      result = evaluated;
      break;
    }
    rt.frames.push({ kind: 'let', param: name, value: evaluated });
    bound++;
  }
  result ??= evaluateNode(ast.body, rt);
  rt.frames.length -= bound;
  return result;
}

/** Innermost REDUCE whose list the ref is a member of ("transactions.distributions" → the transactions frame). */
function memberFrame(frames: Frame[], ref: string): Extract<Frame, { kind: 'reduce' }> | undefined {
  return frames.findLast(
    (frame): frame is Extract<Frame, { kind: 'reduce' }> =>
      frame.kind === 'reduce' && ref.startsWith(`${frame.list}.`)
  );
}

/** The raw value behind a field ref: the current REDUCE item's member where the ref names one, else the row's field. */
function rawFieldValue(ref: string, rt: Runtime): unknown {
  const frame = memberFrame(rt.frames, ref);
  return frame ? frame.item?.[ref.slice(frame.list.length + 1)] : rt.resolveField(ref);
}

function applyBinary(op: string, left: EvalValue, right: EvalValue): EvalValue | FormulaError {
  if (op === '=' || op === '<>') {
    // Equality is total: a blank equals a blank and nothing else, and (as in
    // Excel) text never equals a number — 1 = "1" is FALSE.
    const equal = left === BLANK || right === BLANK ? left === right : valuesEqual(left, right);
    return (op === '=') === equal;
  }
  if (left === BLANK || right === BLANK) {
    return BLANK;
  }
  if (op === '<' || op === '<=' || op === '>' || op === '>=') {
    const cmp = compareValues(left, right);
    if (isFormulaError(cmp)) {
      return cmp;
    }
    switch (op) {
      case '<':
        return cmp < 0;
      case '<=':
        return cmp <= 0;
      case '>':
        return cmp > 0;
      default:
        return cmp >= 0;
    }
  }
  const leftNum = requireNumber(left);
  if (isFormulaError(leftNum)) {
    return leftNum;
  }
  const rightNum = requireNumber(right);
  if (isFormulaError(rightNum)) {
    return rightNum;
  }
  switch (op) {
    case '+':
      return finiteOrNumError(leftNum + rightNum);
    case '-':
      return finiteOrNumError(leftNum - rightNum);
    case '*':
      return finiteOrNumError(leftNum * rightNum);
    case '/':
      return rightNum === 0 ? new FormulaError('#DIV/0!') : finiteOrNumError(leftNum / rightNum);
    case '^':
      return finiteOrNumError(Math.pow(leftNum, rightNum));
    default:
      return new FormulaError('#ERROR!', `Unknown operator "${op}"`);
  }
}

/**
 * The special forms run here — they need laziness (IF, IFBLANK), catching
 * (IFERROR) or scope frames (REDUCE). Everything else is a registered
 * function: its arguments are resolved by param kind, the first error in
 * source order winning, then a blank unless the entry handles blanks, and
 * handed to the entry's own evaluate.
 */
function applyCall(ast: Extract<AstNode, { kind: 'call' }>, rt: Runtime): EvalValue | FormulaError {
  const { fn, args } = ast;
  if (fn === 'IFERROR') {
    const value = evaluateNode(args[0], rt);
    return isFormulaError(value) ? evaluateNode(args[1], rt) : value;
  }
  if (fn === 'IFBLANK') {
    const value = evaluateNode(args[0], rt);
    return value === BLANK ? evaluateNode(args[1], rt) : value;
  }
  if (fn === 'IF') {
    const condition = evaluateNode(args[0], rt);
    if (isFormulaError(condition) || condition === BLANK) {
      return condition;
    }
    const truth = requireBoolean(condition);
    if (isFormulaError(truth)) {
      return truth;
    }
    if (truth) {
      return evaluateNode(args[1], rt);
    }
    return args[2] ? evaluateNode(args[2], rt) : BLANK;
  }
  if (fn === 'REDUCE') {
    return applyReduce(args, rt);
  }
  const spec = rt.registry.get(fn);
  if (!spec?.evaluate) {
    return new FormulaError('#ERROR!', `Unknown function "${fn}"`);
  }
  const resolved = resolveArgs(spec, args, rt);
  if (isFormulaError(resolved) || resolved === BLANK) {
    return resolved;
  }
  return spec.evaluate(resolved, {
    resolveField: rt.resolveField,
    read: (raw, ref) => readValue(raw, ref, rt),
    asOfDate: rt.context?.asOfDate,
  });
}

/**
 * Each argument shaped by its param kind. Every argument is evaluated, so
 * the first error in source order wins over any blank; a blank value then
 * makes the call blank unless the entry handles blanks itself.
 */
function resolveArgs(
  spec: FunctionSpec,
  args: AstNode[],
  rt: Runtime
): CallArg[] | FormulaError | typeof BLANK {
  const usage = new FormulaError('#ERROR!', `${spec.name} expects ${spec.signature}`);
  const resolved: CallArg[] = [];
  let list: { ref: string } | undefined;
  let blank = false;
  for (let i = 0; i < args.length; i++) {
    const node = args[i];
    const param = paramAt(spec, i);
    switch (param?.kind) {
      case 'list': {
        if (node.kind !== 'field') {
          return usage;
        }
        const items = resolveList(node.ref, rt.resolveField);
        if (isFormulaError(items)) {
          return items;
        }
        list = { ref: node.ref };
        resolved.push({ kind: 'list', ref: node.ref, items });
        break;
      }
      case 'member': {
        if (node.kind !== 'field' || !list) {
          return usage;
        }
        if (!node.ref.startsWith(`${list.ref}.`)) {
          // The runtime mirror of typecheck's member-of-its-list rule.
          return new FormulaError('#VALUE!', `Field [${node.ref}] is not a field of [${list.ref}]`);
        }
        resolved.push({
          kind: 'member',
          ref: node.ref,
          list: list.ref,
          key: node.ref.slice(list.ref.length + 1),
        });
        break;
      }
      case 'kpi':
      case 'token':
        if (node.kind !== 'field') {
          return usage;
        }
        resolved.push({ kind: 'ref', ref: node.ref });
        break;
      case 'lambda':
        return usage;
      default: {
        const value = evaluateNode(node, rt);
        if (isFormulaError(value)) {
          return value;
        }
        blank ||= value === BLANK;
        resolved.push({ kind: 'value', value });
      }
    }
  }
  return blank && spec.blanks !== 'handle' ? BLANK : resolved;
}

/**
 * REDUCE(initial, [list], LAMBDA(acc, body)): fold the body over the list's
 * items with `acc` bound to the running value and the list's members readable
 * from the current item. A blank list yields `initial`; the first error in the
 * body ends the fold. The running value may be text (e.g. built with &).
 */
function applyReduce(args: AstNode[], rt: Runtime): EvalValue | FormulaError {
  const [initialNode, listNode, lambdaNode] = args;
  if (listNode?.kind !== 'field' || lambdaNode?.kind !== 'lambda') {
    return new FormulaError('#ERROR!', 'REDUCE expects REDUCE(initial, [list], LAMBDA(acc, expression))');
  }
  const initial = evaluateNode(initialNode, rt);
  if (isFormulaError(initial)) {
    return initial;
  }
  const items = resolveList(listNode.ref, rt.resolveField);
  if (isFormulaError(items)) {
    return items;
  }
  // One parameter today, the accumulator; a wider LAMBDA(acc, item, …) would bind the rest here.
  const frame: Frame = {
    kind: 'reduce',
    param: lambdaNode.params[0],
    acc: initial,
    list: listNode.ref,
    item: undefined,
  };
  rt.frames.push(frame);
  let result: EvalValue | FormulaError = initial;
  for (const item of items) {
    frame.item = item;
    result = evaluateNode(lambdaNode.body, rt);
    if (isFormulaError(result)) {
      break;
    }
    frame.acc = result;
  }
  rt.frames.pop();
  return result;
}

/** A call's list argument resolved to its items: null/absent counts as empty, a scalar is #VALUE!. */
function resolveList(ref: string, resolveField: FieldResolver): ListItem[] | FormulaError {
  const items = resolveField(ref) ?? [];
  if (isFormulaError(items)) {
    return items;
  }
  if (!Array.isArray(items)) {
    return new FormulaError('#VALUE!', `Field [${ref}] is not a list`);
  }
  return items;
}
